import { useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    fetchAlertRules,
    fetchAlertConditions,
    fetchAlertWorkspaces,
    createAlertRule,
    updateAlertRule,
    deleteAlertRule,
    toggleAlertRule,
    reorderAlertRules,
    clearAlertRulesError,
    setAlertRulesOrder,
    optimisticToggleRule,
    resetOperationStatus,
} from '../features/configuration/configurationSlice';

/**
 * Custom hook for managing Alert Rules with API integration
 * Provides all CRUD operations, loading states, and error handling
 */
export const useAlertRules = (filters = {}) => {
    const dispatch = useDispatch();

    // Selectors
    const alertRules = useSelector((state) => state.configuration.alertRules);
    const alertRulesStatus = useSelector((state) => state.configuration.alertRulesStatus);
    const alertRulesError = useSelector((state) => state.configuration.alertRulesError);
    const pagination = useSelector((state) => state.configuration.alertRulesPagination);

    const alertConditions = useSelector((state) => state.configuration.alertConditions);
    const alertConditionsStatus = useSelector((state) => state.configuration.alertConditionsStatus);

    const alertWorkspaces = useSelector((state) => state.configuration.alertWorkspaces);
    const alertWorkspacesStatus = useSelector((state) => state.configuration.alertWorkspacesStatus);

    const createRuleStatus = useSelector((state) => state.configuration.createRuleStatus);
    const createRuleError = useSelector((state) => state.configuration.createRuleError);
    const updateRuleStatus = useSelector((state) => state.configuration.updateRuleStatus);
    const updateRuleError = useSelector((state) => state.configuration.updateRuleError);
    const deleteRuleStatus = useSelector((state) => state.configuration.deleteRuleStatus);
    const deleteRuleError = useSelector((state) => state.configuration.deleteRuleError);

    // Derived states
    const isLoading = alertRulesStatus === 'loading';
    const isConditionsLoading = alertConditionsStatus === 'loading';
    const isWorkspacesLoading = alertWorkspacesStatus === 'loading';
    const isCreating = createRuleStatus === 'loading';
    const isUpdating = updateRuleStatus === 'loading';
    const isDeleting = deleteRuleStatus === 'loading';
    const isSaving = isCreating || isUpdating;

    // Fetch rules on mount
    useEffect(() => {
        if (alertRulesStatus === 'idle') {
            dispatch(fetchAlertRules(filters));
        }
    }, [dispatch, alertRulesStatus, filters]);

    // Fetch conditions on mount
    useEffect(() => {
        if (alertConditionsStatus === 'idle') {
            dispatch(fetchAlertConditions());
        }
    }, [dispatch, alertConditionsStatus]);

    // Fetch workspaces on mount
    useEffect(() => {
        if (alertWorkspacesStatus === 'idle') {
            dispatch(fetchAlertWorkspaces());
        }
    }, [dispatch, alertWorkspacesStatus]);

    // Refetch rules
    const refetch = useCallback(() => {
        dispatch(fetchAlertRules(filters));
    }, [dispatch, filters]);

    // Create a new rule
    const handleCreateRule = useCallback(async (ruleData) => {
        try {
            const result = await dispatch(createAlertRule(ruleData)).unwrap();
            return { success: true, data: result };
        } catch (error) {
            return { success: false, error };
        }
    }, [dispatch]);

    // Update an existing rule
    const handleUpdateRule = useCallback(async (ruleId, updates) => {
        try {
            const result = await dispatch(updateAlertRule({ ruleId, updates })).unwrap();
            return { success: true, data: result };
        } catch (error) {
            return { success: false, error };
        }
    }, [dispatch]);

    // Delete a rule
    const handleDeleteRule = useCallback(async (ruleId) => {
        try {
            await dispatch(deleteAlertRule(ruleId)).unwrap();
            return { success: true };
        } catch (error) {
            return { success: false, error };
        }
    }, [dispatch]);

    // Toggle rule enabled/disabled with optimistic update
    const handleToggleRule = useCallback(async (ruleId, enabled) => {
        // Optimistic update
        dispatch(optimisticToggleRule({ ruleId, enabled }));

        try {
            await dispatch(toggleAlertRule({ ruleId, enabled })).unwrap();
            return { success: true };
        } catch (error) {
            // Revert optimistic update on failure
            dispatch(optimisticToggleRule({ ruleId, enabled: !enabled }));
            return { success: false, error };
        }
    }, [dispatch]);

    // Reorder rules (after drag & drop)
    const handleReorderRules = useCallback(async (newOrder) => {
        // Optimistic update
        dispatch(setAlertRulesOrder(newOrder));

        try {
            await dispatch(reorderAlertRules(newOrder)).unwrap();
            return { success: true };
        } catch (error) {
            // Refetch to restore original order on failure
            dispatch(fetchAlertRules(filters));
            return { success: false, error };
        }
    }, [dispatch, filters]);

    // Clear errors
    const clearErrors = useCallback(() => {
        dispatch(clearAlertRulesError());
    }, [dispatch]);

    // Reset operation status (useful after modal close)
    const resetStatus = useCallback(() => {
        dispatch(resetOperationStatus());
    }, [dispatch]);

    // Move rule up in priority
    const moveRuleUp = useCallback((index) => {
        if (index === 0) return;
        const newRules = [...alertRules];
        [newRules[index - 1], newRules[index]] = [newRules[index], newRules[index - 1]];
        handleReorderRules(newRules);
    }, [alertRules, handleReorderRules]);

    // Move rule down in priority
    const moveRuleDown = useCallback((index) => {
        if (index === alertRules.length - 1) return;
        const newRules = [...alertRules];
        [newRules[index], newRules[index + 1]] = [newRules[index + 1], newRules[index]];
        handleReorderRules(newRules);
    }, [alertRules, handleReorderRules]);

    return {
        // Data
        rules: alertRules,
        conditions: alertConditions,
        workspaces: alertWorkspaces,
        pagination,

        // Loading states
        isLoading,
        isConditionsLoading,
        isWorkspacesLoading,
        isCreating,
        isUpdating,
        isDeleting,
        isSaving,

        // Errors
        error: alertRulesError,
        createError: createRuleError,
        updateError: updateRuleError,
        deleteError: deleteRuleError,

        // Actions
        refetch,
        createRule: handleCreateRule,
        updateRule: handleUpdateRule,
        deleteRule: handleDeleteRule,
        toggleRule: handleToggleRule,
        reorderRules: handleReorderRules,
        moveRuleUp,
        moveRuleDown,

        // Utilities
        clearErrors,
        resetStatus,
    };
};

export default useAlertRules;

